<script lang="ts">
  // Accordion (DisclosureRow) — the design-system disclosure-row primitive
  // (wiki/feature-design-system.md §Component primitives, AC4). A single
  // expand/collapse row: a clickable header (title + optional leading glyph +
  // optional subtitle) and a body revealed when open. The Instrument's
  // seven-keys cards and the Body-Language guide collapsibles compose it. The
  // whole header row toggles (the button stretches over it); an optional
  // `aside` note + action sits beside the button, since a control may not nest
  // inside one. Body content rides in via a snippet, never a slot.
  import type { LucideIcon } from '$lib/ui/icons';
  import type { Snippet } from 'svelte';
  import Button from './Button.svelte';

  interface Props {
    /** The row title. */
    title: string;
    /** Optional secondary line under the title (e.g. the key tagline). */
    subtitle?: string;
    /** Optional leading glyph / emoji (decorative). */
    glyph?: string;
    /** Optional leading icon component (decorative) — drawn in `currentColor`. */
    icon?: LucideIcon;
    /** Open state (bindable). */
    open?: boolean;
    /** Fired after the open state toggles. */
    onToggle?: (open: boolean) => void;
    /** Accessible name for the toggle (defaults to `title`). */
    ariaLabel?: string;
    aside?: { note: string; label: string; onClick: () => void } | null;
    class?: string;
    /** The disclosed body content. */
    children: Snippet;
  }

  let {
    title,
    subtitle,
    glyph,
    icon: Icon,
    open = $bindable(false),
    onToggle,
    ariaLabel,
    aside = null,
    class: klass = '',
    children,
  }: Props = $props();

  function toggle(): void {
    open = !open;
    onToggle?.(open);
  }
</script>

<div class="vd-accordion {klass}" class:vd-accordion--open={open}>
  <div class="vd-accordion__bar" class:vd-accordion__bar--aside={aside}>
    <button
      type="button"
      class="vd-accordion__head"
      aria-expanded={open}
      aria-label={ariaLabel ?? title}
      onclick={toggle}
    >
      {#if Icon}<span class="vd-accordion__glyph" aria-hidden="true"><Icon size={20} /></span>
      {:else if glyph}<span class="vd-accordion__glyph" aria-hidden="true">{glyph}</span>{/if}
      <span class="vd-accordion__text">
        <span class="vd-accordion__title">{title}</span>
        {#if subtitle}<span class="vd-accordion__subtitle">{subtitle}</span>{/if}
      </span>
    </button>
    {#if aside}
      <span class="vd-accordion__note">{aside.note}</span>
      <Button variant="link" class="vd-accordion__action" onclick={aside.onClick}>
        {aside.label}
      </Button>
    {/if}
    <span class="vd-accordion__chevron" aria-hidden="true">{open ? '−' : '+'}</span>
  </div>
  {#if open}
    <div class="vd-accordion__body">
      {@render children()}
    </div>
  {/if}
</div>

<style lang="scss">
  .vd-accordion {
    border: 1px solid var(--line);
    border-radius: 12px;
    background: var(--paper);
    overflow: hidden;
  }

  .vd-accordion__bar {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0 1rem;
    font-family: var(--font-ui);
    transition: background 0.2s ease;

    &:hover {
      background: color-mix(in srgb, var(--accent) 6%, transparent);
    }
  }

  .vd-accordion__head {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.85rem 0;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    color: inherit;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
    }

    &:focus-visible {
      outline: none;

      &::after {
        outline: 2px solid var(--accent-text);
        outline-offset: -2px;
      }
    }
  }

  .vd-accordion__bar--aside .vd-accordion__head {
    flex: none;
  }

  .vd-accordion__note {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--text-meta);
    color: var(--ink-mute);
  }

  .vd-accordion__bar :global(.vd-accordion__action) {
    position: relative;
    z-index: 1;
    min-height: 44px;
  }

  // Too narrow to share the title's line, the note drops to its own line.
  @media (max-width: 640px) {
    .vd-accordion__bar--aside {
      flex-wrap: wrap;
      row-gap: 0;

      .vd-accordion__head {
        flex: 1;
      }

      .vd-accordion__note {
        order: 1;
        flex-basis: 100%;
        margin-top: -0.6rem;
        padding-bottom: 0.85rem;
        white-space: normal;
        overflow-wrap: anywhere;
      }
    }
  }

  .vd-accordion__glyph {
    display: flex;
    font-size: 1.6rem;
    line-height: 1;
    flex: none;
    color: var(--accent-text);
  }

  .vd-accordion__text {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  .vd-accordion__title {
    font-weight: 700;
    color: var(--ink);
  }

  .vd-accordion__subtitle {
    font-size: var(--text-body);
    color: var(--ink-soft);
  }

  .vd-accordion__chevron {
    flex: none;
    font-size: 1.25rem;
    color: var(--accent-text);
    width: 1.25rem;
    text-align: center;
  }

  .vd-accordion__body {
    padding: 0 1rem 1rem;
    border-top: 1px solid var(--line);
  }
</style>
